import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import { childMenuUrl, isInlineMenuGroup, isMenuItemActive } from '../../utils/menu'

function MenuLink({ child, pathname, onNavigate, nested = false }) {
  const { localized } = useLanguage()
  const url = childMenuUrl(child)
  if (!url) return null

  const active = pathname === url

  return (
    <Link
      to={url}
      onClick={onNavigate}
      className={`block rounded-xl text-sm transition-colors hover:bg-primary/5 hover:text-primary ${
        nested ? 'px-3 py-2 ps-5' : 'px-3 py-2.5'
      } ${active ? 'bg-primary/5 font-semibold text-primary' : 'text-text/75'}`}
    >
      {localized(child, 'title')}
    </Link>
  )
}

function MenuSection({ group, pathname, onNavigate }) {
  const { localized } = useLanguage()
  const sectionUrl = childMenuUrl(group)

  return (
    <div className="py-1">
      {sectionUrl ? (
        <Link
          to={sectionUrl}
          onClick={onNavigate}
          className="mb-1 block px-3 text-xs font-semibold uppercase tracking-wider text-primary/70 transition-colors hover:text-primary"
        >
          {localized(group, 'title')}
        </Link>
      ) : (
        <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-primary/70">
          {localized(group, 'title')}
        </p>
      )}
      {group.children.map((child) => (
        <MenuLink
          key={child.id || child.slug}
          child={child}
          pathname={pathname}
          onNavigate={onNavigate}
          nested
        />
      ))}
    </div>
  )
}

function MenuGroup({ group, pathname, onNavigate }) {
  const { localized, isRTL } = useLanguage()
  const [open, setOpen] = useState(false)

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-primary/5 hover:text-primary ${
          isMenuItemActive(group, pathname) ? 'bg-primary/5 font-semibold text-primary' : 'text-text/75'
        }`}
      >
        <span>{localized(group, 'title')}</span>
        <ChevronRight className={`h-4 w-4 opacity-60 ${isRTL ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 8 : -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRTL ? 8 : -8 }}
            transition={{ duration: 0.15 }}
            className={`absolute top-0 z-10 min-w-[240px] rounded-2xl border border-border bg-white p-2 shadow-xl shadow-primary/10 ${
              isRTL ? 'right-full me-2' : 'left-full ms-2'
            }`}
          >
            {group.children.map((child) => (
              <MenuLink
                key={child.id || child.slug}
                child={child}
                pathname={pathname}
                onNavigate={onNavigate}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function NavDropdown({ item, isTransparent, location }) {
  const { localized } = useLanguage()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const label = localized(item, 'title')
  const children = item.children || []
  const hubUrl = item.url || (item.slug ? `/pages/${item.slug}` : null)
  const sectionActive =
    (hubUrl && location.pathname === hubUrl) ||
    children.some((child) => isMenuItemActive(child, location.pathname))

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const triggerClass = isTransparent
    ? sectionActive || open
      ? 'text-accent'
      : 'text-white/90 hover:text-white'
    : sectionActive || open
      ? 'text-primary'
      : 'text-text/70 hover:text-primary'

  const closeMenu = () => setOpen(false)

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div className={`relative inline-flex items-center gap-0.5 rounded-lg text-sm font-medium ${triggerClass}`}>
        {hubUrl ? (
          <Link to={hubUrl} className="px-3 py-2 transition-colors">
            {label}
          </Link>
        ) : (
          <span className="cursor-default px-3 py-2">{label}</span>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="px-1 py-2"
          aria-expanded={open}
          aria-label={`${label} submenu`}
        >
          <ChevronDown className={`h-4 w-4 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {sectionActive && (
          <motion.span
            layoutId="nav-indicator"
            className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full bg-accent"
          />
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute start-0 top-full z-50 pt-2"
          >
            <div className="min-w-[420px] rounded-2xl border border-border bg-white p-2 shadow-xl shadow-primary/10">
              {(() => {
                const inlineGroups = children.filter(isInlineMenuGroup)
                const flatLinks = children.filter((child) => !child.children?.length)
                const nestedGroups = children.filter(
                  (child) => child.children?.length && !isInlineMenuGroup(child)
                )

                return (
                  <>
                    {inlineGroups.length > 0 && (
                      <div
                        className={`grid gap-x-6 gap-y-1 ${
                          inlineGroups.length > 1 ? 'grid-cols-2' : 'grid-cols-1'
                        }`}
                      >
                        {inlineGroups.map((group) => (
                          <MenuSection
                            key={group.id || group.slug}
                            group={group}
                            pathname={location.pathname}
                            onNavigate={closeMenu}
                          />
                        ))}
                      </div>
                    )}
                    {flatLinks.map((child) => (
                      <MenuLink
                        key={child.id || child.slug}
                        child={child}
                        pathname={location.pathname}
                        onNavigate={closeMenu}
                      />
                    ))}
                    {nestedGroups.map((child) => (
                      <MenuGroup
                        key={child.id || child.slug}
                        group={child}
                        pathname={location.pathname}
                        onNavigate={closeMenu}
                      />
                    ))}
                  </>
                )
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
